import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CompanyModel } from '@data-layer/models/companies';
import { enumShippingRole, enumUserBetaFlag, enumUserRole } from '@data-layer/enums/user';
import { enumCompanyBusinessType } from '@app/data-layer/enums/companies';
import { requiresPhotoId } from '@constants/business-types';
import { Router } from '@angular/router';
import { CompaniesService } from '@services/companies.service';
import { Observable, ReplaySubject, from } from 'rxjs';
import { GenericService } from '@services/generic.service';
import { AlertService } from '@services/alert.service';
import { finalize, map, switchMap, take, tap, takeUntil, mergeMap } from 'rxjs/operators';
import { DocumentAccessCredentialsModel } from '@data-layer/models/documents';
import { CompaniesRepository, CompanyLogoRepository, DocumentsRepository } from '@data-layer/repositories';
import { UserBetaFlagModel, UserModel } from '@app/data-layer/models/user';
import { OrganizationsRepository } from '@app/data-layer/repositories/organizations.repository';
import { OrganizationMemberModel } from '@app/data-layer/models/organizations/organization-member-model';
import { enumOrganizationRole } from '@app/data-layer/enums/organizations';
import { DependencyModel } from '@app/data-layer/models/dependencies';
import { UtilsHelper } from '@helpers/utils-helper';
import { DateHelper } from '@helpers/date.helper';
import { enumDependencyState } from '@app/data-layer/enums/dependencies';
import { DependenciesService } from '@services/dependencies.service';
import { OrganizationModel } from '@app/data-layer/models/organizations';
import { OrganizationsService } from '@services/organizations.service';
import { ACCESS_TOKEN } from '@constants/setting-keys.const';
import { JwtHelperService } from '@auth0/angular-jwt';
import { enumCbpAssignedNumberStatus } from '@app/data-layer/enums/common';
import { TranslateService } from '@ngx-translate/core';
import { Select } from '@ngxs/store';
import { SharedSelectors } from '@app/shared/shared-state';

@Component({
  selector: 'account-settings-company-page',
  templateUrl: './account-settings-company-page.component.html',
  styleUrls: ['./account-settings-company-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSettingsCompanyPageComponent implements OnInit, OnDestroy {
  @Select(SharedSelectors.getAppBetaFlags) public appBetaFlags$: Observable<UserBetaFlagModel[]>;

  public organization: OrganizationModel;
  public companies: CompanyModel[];
  public organizationMembers: OrganizationMemberModel[];
  public companiesLastDependency: { number: DependencyModel } | {} = {};
  public isTablet$: Observable<boolean>;
  public enumShippingRole: typeof enumShippingRole = enumShippingRole;
  public enumUserRole: typeof enumUserRole = enumUserRole;
  public enumCompanyBusinessType: typeof enumCompanyBusinessType = enumCompanyBusinessType;
  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  public enumOrganizationMemberRole: typeof enumOrganizationRole = enumOrganizationRole;
  public isLoadingLogoIds: number[] = [];
  public isHasOnboardingRedesign: boolean = false;
  public currentUser: UserModel;
  public isDeleteModalOpen: boolean = false;
  public companyToDelete: CompanyModel = null;
  public isDeleting: boolean = false;
  private currentUserMember: OrganizationMemberModel;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private router: Router,
    private companiesService: CompaniesService,
    private companiesRepository: CompaniesRepository,
    private genericService: GenericService,
    private alertService: AlertService,
    private dependenciesService: DependenciesService,
    private companyLogoRepository: CompanyLogoRepository,
    private organizationsService: OrganizationsService,
    private organizationsRepository: OrganizationsRepository,
    private documentsRepository: DocumentsRepository,
    private jwtHelperService: JwtHelperService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.isTablet$ = this.genericService.isTablet$;

    const firstCompany = this.companiesService.getCompanies()[0];
    this.currentUser = this.companiesService.getUser();
    this.organizationsService
      .getOrganizationByIdFromServer(this.currentUser.defaultOrganizationId || firstCompany?.organizationId)
      .pipe(
        tap((organization) => {
          this.organization = organization;
          this.companies = organization.companies;
          this.sortAlphabeticallyByBusinessName();
          this.organizationsService.setDefaultOrganization(organization);
        }),
        switchMap((organization) => this.organizationsRepository.getOrganizationMembers(organization.id)),
        takeUntil(this.destroy$),
      )
      .subscribe((organizationMembers) => {
        this.currentUserMember = organizationMembers?.find((member) => member.user.userId === this.currentUser.uid);
        this.organizationMembers = organizationMembers;
        this.cdr.markForCheck();

        const companies$ = from(this.companies);
        const processedCompanyDependencies$ = companies$.pipe(
          mergeMap((company) => this.getCompanyDependencies(company.id)),
          takeUntil(this.destroy$),
        );

        processedCompanyDependencies$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
          const { companyId, dependencies } = result;
          this.companiesLastDependency[companyId] = {
            ...UtilsHelper.maxElementBy<DependencyModel, string>(dependencies, 'timestamp', DateHelper.getTime),
          };
          this.cdr.markForCheck();
        });
      });

    this.appBetaFlags$.pipe(takeUntil(this.destroy$)).subscribe((flags: UserBetaFlagModel[]) => {
      this.isHasOnboardingRedesign = flags.map((flag: UserBetaFlagModel) => flag.tag).includes(enumUserBetaFlag.onboardingRedesign);
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public getCompanyAddressLine(company: CompanyModel): string {
    return this.companiesService.getCompanyAddressLine(company);
  }

  public canDeleteCompany(company: CompanyModel): boolean {
    return !!company.canDelete && (this.companies?.length ?? 0) > 1;
  }

  public getOrganizationCompanyMembers(companyId: number): OrganizationMemberModel[] {
    return (this.organizationMembers ?? [])
      .filter((organizationMember) => organizationMember.company?.id === companyId)
      .filter((member, index, self) => {
        return index === self.findIndex((m) => m.user.userId === member.user.userId);
      });
  }

  public redirectToCompanyView(companyId: number): void {
    this.companiesService
      .getCompanyAsync(true, true, companyId)
      .pipe(
        switchMap((company) => this.loadUserBetaFlags(company)),
        takeUntil(this.destroy$),
      )
      .subscribe((betaFlags) => {
        const selectedCompany = this.companies.find((company) => company.id === companyId);
        this.companiesService.setDefaultCompany(selectedCompany);
        this.companiesService.setUserBetaFlags(betaFlags);
        this.router.navigate(['/companies', 'account-settings', 'company-view', companyId]);
      });
  }

  public redirectToCreateCompany(): void {
    this.router.navigate(['companies', 'account-settings', 'create']);
  }

  public onShowDeleteConfirm(company: CompanyModel): void {
    this.companyToDelete = company;
    this.isDeleteModalOpen = true;
  }

  public onHideDeleteConfirm(): void {
    if (this.isDeleting) {
      return;
    }
    this.companyToDelete = null;
    this.isDeleteModalOpen = false;
  }

  public onDeleteCompany(): void {
    if (!this.companyToDelete) {
      return;
    }
    const companyIdToDelete = this.companyToDelete.id;
    this.isDeleting = true;
    this.companiesRepository
      .deleteCompany(companyIdToDelete)
      .pipe(
        finalize(() => {
          this.isDeleting = false;
          this.isDeleteModalOpen = false;
          this.companyToDelete = null;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.companies = this.companies.filter((c) => c.id !== companyIdToDelete);
          this.companiesService.syncAfterCompanyDeleted(companyIdToDelete).pipe(takeUntil(this.destroy$)).subscribe();
        },
        error: () => {
          this.alertService.error();
        },
      });
  }

  public uploadCompanyLogo(file: File, companyId: number): void {
    this.setLogoIsLoading(companyId, true);

    this.uploadLogoToServer(file, companyId)
      .pipe(
        finalize(() => this.setLogoIsLoading(companyId, false)),
        switchMap(() => this.organizationsService.getOrganizationByIdFromServer(this.organization.id)),
        takeUntil(this.destroy$),
      )
      .subscribe((organization) => {
        this.companies = organization.companies;
        this.sortAlphabeticallyByBusinessName();
        this.cdr.markForCheck();
      });
  }

  public isLoadingLogo(companyId: number): boolean {
    return !!this.isLoadingLogoIds.find((item: number) => item === companyId);
  }

  // #13188 — Individual and Sole Proprietor use the person-icon placeholder instead of the logo uploader
  public isPhotoIdBusinessType(businessType: enumCompanyBusinessType): boolean {
    return requiresPhotoId(businessType);
  }

  public getCompanyIdentifier(company: CompanyModel): string {
    if (this.isHasAssignedCbpRequest && !!company?.cbpAssignedNumberRegistration) {
      switch (company?.cbpAssignedNumberRegistration.status) {
        case enumCbpAssignedNumberStatus.SENT:
        case enumCbpAssignedNumberStatus.SENDING:
          return this.translateService.instant('LABELS.ASSIGNED_CBP_PENDING');
        case enumCbpAssignedNumberStatus.COMPLETED:
        default:
          return company?.identifier;
      }
    } else {
      return company?.identifier;
    }
  }

  public isCompanyWithCbpAssignedNumberRequested(company: CompanyModel): boolean {
    return (
      this.isHasAssignedCbpRequest &&
      (company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENT ||
        company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENDING ||
        company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.COMPLETED)
    );
  }

  public get isOrganizationAdmin(): boolean {
    return this.currentUserMember?.role === enumOrganizationRole.ADMIN;
  }

  public isCbpRegistrationPending(company: CompanyModel): boolean {
    return (
      this.isHasAssignedCbpRequest &&
      (company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENT ||
        company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENDING)
    );
  }

  public isCbpRegistrationCompleted(company: CompanyModel): boolean {
    return this.isHasAssignedCbpRequest && company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.COMPLETED;
  }

  public isCbpRegistrationRejected(company: CompanyModel): boolean {
    return this.isHasAssignedCbpRequest && company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.REJECTED;
  }

  public registrationCompanyTextColor(companyId: number): string {
    switch (this.companiesLastDependency[companyId].state) {
      case enumDependencyState.REJECTED:
        return '#ff5c5c';
      case enumDependencyState.ACCEPTED:
        return '#27ae60';
      case enumDependencyState.PROVIDER_SEND_PENDING:
        return '#ff9500';
    }
  }

  public registrationCompanyText(companyId: number): string {
    switch (this.companiesLastDependency[companyId].state) {
      case enumDependencyState.REJECTED:
        return 'LABELS.REGISTRATION_REJECTED';
      case enumDependencyState.ACCEPTED:
        return 'DEPENDENCY_STATE.ACCEPTED';
      case enumDependencyState.PROVIDER_SEND_PENDING:
        return 'LABELS.REGISTRATION_PENDING';
    }
  }

  private getCompanyDependencies(companyId: number): Observable<{ companyId: number; dependencies: DependencyModel[] }> {
    return this.dependenciesService.getCompanyDependencies(companyId).pipe(
      map((dependencies) => {
        return {
          companyId,
          dependencies,
        };
      }),
      takeUntil(this.destroy$),
    );
  }

  private uploadLogoToServer(file: File, companyId: number): Observable<CompanyModel> {
    return this.companyLogoRepository.getCredentials(companyId).pipe(
      switchMap((credentials: DocumentAccessCredentialsModel) => {
        return this.documentsRepository.uploadFileByUrl(credentials.uploadUrl, file, false).pipe(map(() => credentials));
      }),
      switchMap((credentials) => {
        return this.companyLogoRepository.setUrl(companyId, credentials.downloadUrl);
      }),
      switchMap(() => this.companiesService.getCompanyAsync(true, false, companyId)),
      take(1),
    );
  }

  private setLogoIsLoading(companyId: number, isLoading: boolean): void {
    if (isLoading) {
      this.isLoadingLogoIds.push(companyId);
    } else {
      this.isLoadingLogoIds = this.isLoadingLogoIds.filter((item: number) => item !== companyId);
    }
  }

  private loadUserBetaFlags(company?: CompanyModel): Observable<UserBetaFlagModel[]> {
    return this.companiesService.getUserBetaFlags({
      userExternalId: this.userExternalId,
      companyId: company.id,
      organizationId: company.organizationId,
    });
  }

  private get userExternalId(): string {
    return this.jwtHelperService.decodeToken(localStorage.getItem(ACCESS_TOKEN))?.user_external_id || null;
  }

  private get isHasAssignedCbpRequest(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.assignedCbpRequest);
  }

  private sortAlphabeticallyByBusinessName() {
    this.companies.sort((a, b) => {
      if (a.businessName < b.businessName) {
        return -1;
      }
      if (a.businessName > b.businessName) {
        return 1;
      }
      return 0;
    });
  }
}
